import React, { useState } from "react";

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import FormHelperText from "@mui/material/FormHelperText";
import Button from "@mui/material/Button";

import { useUserAuth } from "../contexts/authContext";

import { ConvertISOToDate, getTimePassedFromIso } from "../util";
import { getHexColorStringFromFeedbackStatus } from "./feedbackUtil";

const FeedbackSubmissionDialog = ({ feedbackSubmissions, setFeedbackSubmissions, selectedFeedbackSubmissionUuid, setSelectedFeedbackSubmissionUuid }) => {
    const [commentInputValue, setCommentInputValue] = useState("");
    const [commentErrorText, setCommentErrorText] = useState("");
    const [statusChangeErrorText, setStatusChangeErrorText] = useState("");

    const { user } = useUserAuth();

    const selectedFeedbackSubmission = feedbackSubmissions.find(({ uuid }) => {
        return selectedFeedbackSubmissionUuid === uuid;
    });
    const selectedFeedbackSubmissionStatusColor = selectedFeedbackSubmission
        ? getHexColorStringFromFeedbackStatus(selectedFeedbackSubmission.status, statusChangeErrorText)
        : "#90caf9";

    const handleStatusChange = async (e) => {
        try {
            if (statusChangeErrorText.length > 0) {
                setStatusChangeErrorText("");
            }
            const updatedSubmission = {
                ...selectedFeedbackSubmission,
                status: e.target.value,
                updated_user_id: user.id
            };
            await handleUpdateSubmission(updatedSubmission);
        } catch (err) {
            console.error(err);
            setStatusChangeErrorText("An error occured while updating this submission. Please try again later.");
        }
    };
    const handleCommentKeyDown = async (e) => {
        try {
            if (e.key !== "Enter") return;
            e.preventDefault();

            if (!commentInputValue.trim().length) {
                return setCommentErrorText("Please provide a comment before submitting.");
            } else if (commentErrorText.length > 0) {
                setCommentErrorText("");
            }
            const newComment = {
                id: user.id,
                comment: commentInputValue,
                comment_date: new Date().toISOString()
            };
            const updatedSubmission = {
                ...selectedFeedbackSubmission,
                comments: [newComment, ...selectedFeedbackSubmission.comments],
                updated_user_id: user.id
            };
            await handleUpdateSubmission(updatedSubmission);
            setCommentInputValue("");
        } catch (err) {
            console.error(err);
            setCommentErrorText("An error occured while publishing this comment. Please try again later.");
        }
    };
    const handleUpdateSubmission = async (submission) => {
        const requestOptions = {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(submission)
        };
        const response = await fetch(`api/feedback/update`, requestOptions);
        if (!response.ok) throw new Error(response);

        const rows = await response.json();
        setFeedbackSubmissions(rows);
    };
    const handleCloseDialog = () => {
        setSelectedFeedbackSubmissionUuid(null);
    };
    return selectedFeedbackSubmission ? (
        <Dialog open={true} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
            <DialogTitle>{`Feedback Submission`}</DialogTitle>

            <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 0 }}>
                <Grid container spacing={2} sx={{ pt: 1 }}>
                    <Grid size={{ xs: 12, md: 3 }}>
                        <FormControl fullWidth sx={{ minWidth: 120 }} size="small" error={statusChangeErrorText.length > 0 ? true : false}>
                            <InputLabel
                                id="status-label"
                                sx={{ color: selectedFeedbackSubmissionStatusColor, "&.Mui-focused": { color: selectedFeedbackSubmissionStatusColor } }}
                            >
                                Status
                            </InputLabel>

                            <Select
                                labelId="status-label"
                                value={selectedFeedbackSubmission.status}
                                label="Status"
                                onChange={handleStatusChange}
                                sx={{
                                    color: selectedFeedbackSubmissionStatusColor,
                                    "& .MuiOutlinedInput-notchedOutline": {
                                        borderColor: selectedFeedbackSubmissionStatusColor
                                    },
                                    "&:hover .MuiOutlinedInput-notchedOutline": {
                                        borderColor: selectedFeedbackSubmissionStatusColor
                                    },
                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                        borderColor: selectedFeedbackSubmissionStatusColor
                                    },
                                    "& .MuiSelect-icon": {
                                        color: selectedFeedbackSubmissionStatusColor
                                    }
                                }}
                            >
                                {["NEW", "IN_REVIEW", "CLOSED"].map((value, i) => (
                                    <MenuItem key={i} value={value}>
                                        {value}
                                    </MenuItem>
                                ))}
                            </Select>
                            <FormHelperText>{statusChangeErrorText}</FormHelperText>
                        </FormControl>
                    </Grid>

                    <Grid size={{ xs: 12, md: 3 }}>
                        <FormControl fullWidth sx={{ minWidth: 120 }} size="small" disabled>
                            <InputLabel id="type-label">Type</InputLabel>

                            <Select labelId="type-label" value={selectedFeedbackSubmission.feedback_type} label="Type">
                                {["FEEDBACK", "FEATURE", "BUG"].map((value, i) => (
                                    <MenuItem key={i} value={value}>
                                        {value}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid size={{ xs: 12, md: 6 }} sx={{ display: "flex", justifyContent: "flex-end" }}>
                        <FormControlLabel
                            sx={{ ml: "auto" }}
                            control={<Checkbox size="small" checked={selectedFeedbackSubmission.can_contact} disabled />}
                            label="Can contact"
                            slotProps={{ typography: { noWrap: true } }}
                        />
                    </Grid>

                    {[
                        {
                            title: "User",
                            value: selectedFeedbackSubmission.user_email,
                            size: { xs: 12, md: 6 }
                        },
                        {
                            title: "Organization",
                            value: selectedFeedbackSubmission.user_organization_name,
                            size: { xs: 12, md: 6 }
                        },
                        {
                            title: "Date Created",
                            value: ConvertISOToDate(selectedFeedbackSubmission.date_created),
                            size: { xs: 12, md: 6 }
                        },
                        {
                            title: "Updated",
                            value: ConvertISOToDate(selectedFeedbackSubmission.date_updated),
                            size: { xs: 12, md: 6 }
                        },
                        {
                            title: "Description",
                            value: selectedFeedbackSubmission.feedback_description,
                            size: { xs: 12 }
                        }
                    ].map(({ title, value, size }, i) => (
                        <Grid key={i} size={size}>
                            <TextField label={title} value={value} disabled={true} size="small" fullWidth multiline minRows={1} />
                        </Grid>
                    ))}
                </Grid>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <Typography gutterBottom variant="body2" fontWeight="bold" color="textPrimary">
                        Comments
                    </Typography>

                    <TextField
                        fullWidth
                        label="Comment"
                        multiline
                        minRows={2}
                        value={commentInputValue}
                        onChange={(e) => setCommentInputValue(e.target.value)}
                        variant="outlined"
                        placeholder={"Add a comment..."}
                        InputLabelProps={{ shrink: true }}
                        autoFocus
                        size="small"
                        onKeyDown={handleCommentKeyDown}
                        error={commentErrorText.length > 0 ? true : false}
                        helperText={commentErrorText.length ? commentErrorText : "Press the enter key to comment"}
                    />

                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        {selectedFeedbackSubmission.comments.map((comment, i) => (
                            <Box key={i}>
                                <Divider />

                                <Box sx={{ display: "flex", pt: 1 }}>
                                    <Typography variant="body2">{comment.user_email}</Typography>

                                    <Typography variant="caption" color="textSecondary" whiteSpace={"pre"}>
                                        {` - ${getTimePassedFromIso(comment.comment_date)}`}
                                    </Typography>
                                </Box>

                                <Typography variant="caption" color="textSecondary">
                                    {comment.comment}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions>
                <Button onClick={() => setSelectedFeedbackSubmissionUuid(null)}>Close</Button>
            </DialogActions>
        </Dialog>
    ) : (
        <Dialog open={true} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
            <DialogTitle>Submission Not Found</DialogTitle>

            <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 0 }}>
                <DialogContentText>We were unable to find this specific feedback submission. Please refresh the page or try again later.</DialogContentText>
            </DialogContent>

            <DialogActions>
                <Button onClick={() => setSelectedFeedbackSubmissionUuid(null)}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

export default FeedbackSubmissionDialog;
